import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form/immutable';
import styled from 'styled-components';
import {
  SelectInput,
  DropdownInput,
  CheckboxInput,
} from '@trueprodigy/components';
import TextInput from '@components/TextInputCaseSetting';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import AddIcon from '@material-ui/icons/Add';
import MultiIdInput from '@modules/appeals/components/MultiIdInput';

const SearchForm = ({
  change,
  config,
  dropdowns,
  handleSubmit,
  formValues,
  addAdvSearchOpt,
  removeAdvSearchOpt,
  advSearchOpts,
  primarySearchFields,
  appraisalYears,
}) => {
  const searchFields = config
    .filter((c) => !primarySearchFields.map((f) => f.name).includes(c.id))
    .filter((c) => !advSearchOpts.map((o) => o.field).includes(c.id));

  const getOperators = () => {
    const selectedField = formValues?.field;

    if (selectedField) {
      return config
        .find((c) => c.id === selectedField)
        .operators.map((v) => ({
          label: v,
          value: v,
        }));
    }
  };

  const getStyle = () => {
    const operator = formValues.operator;
    const field = config.find((c) => c.id === formValues.field);
    if (
      (operator === 'in' ||
        ['schoolCode', 'cityCode', 'taxingUnitCode'].includes(field.id)) &&
      field.id !== 'pid'
    ) {
      return { transform: 'translateY(-23px)' };
    }
    return {};
  };

  const getLabel = ({ field, operator, value }) => {
    const searchConfig = config.find((c) => c.id === field);
    const description = searchConfig?.description;
    return `${description} ${operator} ${value}`;
  };

  const getMetadata = () => {
    const operator = formValues.operator;
    const field = config.find((c) => c.id === formValues.field);
    const metadata = {};

    if (field.id === 'pid') {
      metadata.autoComplete = 'off';
      metadata.propIdsHistory = [];
      metadata.type = 'text';
    }
    metadata.customFilter = (option, value) => {
      return option?.label?.toLowerCase()?.includes(value.toLowerCase());
    };

    if (
      operator === 'in' ||
      ['schoolCode', 'cityCode', 'taxingUnitCode'].includes(field.id)
    ) {
      metadata.isMulti = true;
      metadata.addEmptyOption = false;
    }
    if (field.code) {
      metadata.options = field.codes.map((x) => ({ label: x, value: x }));
    }

    if (
      ['abstractSubdivisionName', 'marketArea', 'asCode'].includes(field.id)
    ) {
      metadata.handleLarge = true;
    }

    if (field.id === 'nextInspectionDate') {
      metadata.type = 'date';
      metadata.InputLabelProps = { shrink: true };
    }
    if (field.codefile && dropdowns && dropdowns.getIn(['data', field.id])) {
      const dropdownsJs = dropdowns.getIn(['data', field.id]).toJS();
      metadata.options = dropdownsJs.map((d) => ({
        label: `${d.codeName} (${d.codeDescription})`,
        value: d.codeName,
      }));
    }

    if (
      field.id === 'abstractSubdivisionName' &&
      dropdowns &&
      dropdowns.getIn(['data', 'asCode'])
    ) {
      const dropdownsJs = dropdowns.getIn(['data', 'asCode']).toJS();
      metadata.options = dropdownsJs.map((item) => ({
        value: item.codeName,
        label: `${item.codeDescription} (${item.codeName})`,
      }));
    }

    if (
      field.id === 'taxingUnitCode' &&
      dropdowns &&
      dropdowns.getIn(['data', 'taxingUnitCode'])
    ) {
      const dropdownsJs = dropdowns.getIn(['data', 'taxingUnitCode']).toJS();
      metadata.options = dropdownsJs
        .filter(
          (d) => d.taxingUnitType !== 'City' && d.taxingUnitType !== 'School',
        )
        .map((item) => ({
          value: item.taxingUnitCode,
          label: `${item.taxingUnitCode} (${item.taxingUnitName})`,
        }));
    }
    if (
      field.id === 'schoolCode' &&
      dropdowns &&
      dropdowns.getIn(['data', 'taxingUnitCode'])
    ) {
      const dropdownsJs = dropdowns.getIn(['data', 'taxingUnitCode']).toJS();
      metadata.options = dropdownsJs
        .filter((d) => d.taxingUnitType === 'School')
        .map((item) => ({
          value: item.taxingUnitCode,
          label: `${item.taxingUnitCode} (${item.taxingUnitName})`,
        }));
    }
    if (
      field.id === 'cityCode' &&
      dropdowns &&
      dropdowns.getIn(['data', 'taxingUnitCode'])
    ) {
      const dropdownsJs = dropdowns.getIn(['data', 'taxingUnitCode']).toJS();
      metadata.options = dropdownsJs
        .filter((d) => d.taxingUnitType === 'City')
        .map((item) => ({
          value: item.taxingUnitCode,
          label: `${item.taxingUnitCode} (${item.taxingUnitName})`,
        }));
    }
    if (
      field.id === 'exemptionCode' &&
      dropdowns &&
      dropdowns.getIn(['data', 'exemptionCode'])
    ) {
      const dropdownsJs = dropdowns.getIn(['data', 'exemptionCode']).toJS();
      metadata.options = dropdownsJs.map((item) => ({
        value: item.exemptionCode,
        label: `${item.exemptionCode} (${item.exemptionDesc})`,
      }));
    }
    if (!field.code) {
      metadata.onKeyPress = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (
            formValues.operator &&
            formValues.field &&
            (isTruthy(formValues.searchValue) ||
              (formValues.from && formValues.to))
          ) {
            addHandler();
            setTimeout(() => {
              handleSubmit();
            }, 300);
          }
        }
      };
    }
    return metadata;
  };

  const isTruthy = (input) => {
    if (Array.isArray(input)) {
      return input.length > 0;
    }
    return Boolean(input);
  };

  const getComponent = () => {
    const operator = formValues.operator;
    const field = config.find((c) => c.id === formValues.field);
    if (field.id === 'pid') {
      return MultiIdInput;
    }
    if (field.code) {
      return SelectInput;
    }
    if (
      operator === 'in' ||
      ['schoolCode', 'cityCode', 'taxingUnitCode'].includes(field.id)
    ) {
      return DropdownInput;
    }
    if (
      [
        'imprvOnly',
        'landOnly',
        'exReset',
        'isUDI',
        'missingGisCentroid',
        'useBuilderAdjustment',
        'excludeConfidential',
        'suReset',
      ].includes(field.id)
    ) {
      return CheckboxInput;
    }
    return TextInput;
  };

  const getValue = () => {
    const operator = formValues.operator;
    const field = config.find((c) => c.id === formValues.field);
    if (
      (operator === 'in' ||
        ['schoolCode', 'cityCode', 'taxingUnitCode'].includes(field.id)) &&
      field.id !== 'pid'
    ) {
      return formValues.searchValue
        ? formValues.searchValue.map((v) => v.value)
        : [];
    }
    return formValues.searchValue || [formValues.from, formValues.to];
  };

  const resetAdvSearch = () => {
    ['field', 'operator', 'searchValue', 'from', 'to'].forEach((v) =>
      change(v, ''),
    );
  };

  const addHandler = () => {
    const value = getValue();
    addAdvSearchOpt({
      field: formValues.field,
      operator: formValues.operator,
      value,
    });
    resetAdvSearch();
  };

  const isValidForm = (formValues) => {
    let isValid = false;
    if (formValues) {
      const keys = Object.keys(formValues);
      for (let x = 0; x < keys.length; x++) {
        if (
          keys[x] !== 'field' &&
          keys[x] !== 'operator' &&
          keys[x] !== 'searchValue'
        ) {
          isValid = true;
        }
      }
    }
    return isValid;
  };

  return (
    <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
      <SearchFormExpansion expanded={true}>
        <FormRow>
          <Field
            component={SelectInput}
            name="field"
            style={{ width: 200 }}
            options={
              searchFields &&
              searchFields.map((f) => ({
                label: f.description,
                value: f.id,
              }))
            }
            onChange={() => {
              ['operator', 'searchValue', 'from', 'to'].forEach((v) =>
                change(v, ''),
              );
            }}
          />
          <Field
            component={SelectInput}
            name="operator"
            style={{ width: 160 }}
            options={getOperators()}
          />
          {formValues.operator && (
            <FieldWrapper style={getStyle()}>
              {formValues.operator === 'between' ? (
                <BetweenWrapper>
                  <Field
                    component={TextInput}
                    placeholder="From"
                    name="from"
                    style={{ width: 150, marginRight: 5 }}
                    type={
                      formValues.field === 'nextInspectionDate'
                        ? 'date'
                        : 'text'
                    }
                  />
                  <Field
                    component={TextInput}
                    placeholder="To"
                    name="to"
                    style={{ width: 150, marginLeft: 5 }}
                    type={
                      formValues.field === 'nextInspectionDate'
                        ? 'date'
                        : 'text'
                    }
                  />
                </BetweenWrapper>
              ) : (
                <Field
                  component={getComponent()}
                  name="searchValue"
                  {...getMetadata()}
                />
              )}
            </FieldWrapper>
          )}
          <IconButton
            color="primary"
            disabled={
              !(
                formValues.operator &&
                formValues.field &&
                (isTruthy(formValues.searchValue) ||
                  (formValues.from && formValues.to))
              )
            }
            onClick={() => {
              addHandler();
            }}
          >
            <AddIcon />
          </IconButton>
        </FormRow>
        <ChipBox>
          {advSearchOpts.map((v, i) => (
            <Chip
              key={`${v.field}-${v.value}`}
              label={getLabel(v)}
              onDelete={() => removeAdvSearchOpt(i)}
              style={{ margin: 5 }}
            />
          ))}
        </ChipBox>
      </SearchFormExpansion>
      <SearchFormContainer>
        {primarySearchFields.map((field, i) => {
          if (field.name === 'pYear') {
            return (
              <Field
                autoFocus={i === 0}
                key={field.name}
                component={SelectInput}
                label={field.label}
                name={field.name}
                style={{ flex: 1, marginRight: 5, marginTop: '-15px' }}
                options={
                  appraisalYears.toJS().data
                    ? [{ value: 'All', label: 'All' }].concat(
                        appraisalYears.toJS().data.map((value) => ({
                          value,
                          label: value,
                        })),
                      )
                    : [{ value: 'All', label: 'All' }]
                }
              />
            );
          }
          return (
            <Field
              autoFocus={i === 0}
              key={field.name}
              component={TextInput}
              label={field.label}
              name={field.name}
              style={{ flex: 1, marginRight: 5 }}
            />
          );
        })}
        {/* <IconButton onClick={() => setSearchExpanded(!searchExpanded)}>
          <ExpandMoreIcon />
        </IconButton> */}
        <Button
          disabled={
            !isValidForm(formValues) &&
            (!advSearchOpts || !advSearchOpts.length)
          }
          variant="contained"
          color="primary"
          style={{ height: 35, marginLeft: 10 }}
          type="submit"
        >
          Search
        </Button>
      </SearchFormContainer>
    </form>
  );
};

SearchForm.propTypes = {
  change: PropTypes.func.isRequired,
  config: PropTypes.arrayOf(PropTypes.object),
  dropdowns: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  formValues: PropTypes.object,
  addAdvSearchOpt: PropTypes.func.isRequired,
  removeAdvSearchOpt: PropTypes.func.isRequired,
  advSearchOpts: PropTypes.array,
  primarySearchFields: PropTypes.arrayOf(PropTypes.object),
  appraisalYears: PropTypes.any,
};

const SearchFormContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchFormExpansion = styled.div`
  align-items: center;
  display: ${({ expanded }) => (expanded ? 'flex' : 'none')};
`;

const FormRow = styled.div`
  display: flex;
  width: 55%;
  align-items: baseline;
`;

const BetweenWrapper = styled.div`
  display: flex;
  align-items: center;
  transform: translateY(-1px);
`;

const ChipBox = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const FieldWrapper = styled.div`
  width: 340px;
  transform: translateY(-3px);

  > div > .MuiFormControl-root + i {
    top: 0;
  }
`;

export default reduxForm({
  form: 'propertyLookupSearch',
  destroyOnUnmount: true,
})(SearchForm);
